
import Vue from 'vue';
import moment from 'moment-timezone';
import { mapActions, mapGetters, mapState } from 'vuex';
import {
	BalanceAccountAutoTopUp,
	calcRealtimeBalanceInfo,
	currencyTokens,
	centsToDollars,
	currency,
	hundredthsToTokens,
	isBelowAutoTopUpThreshold,
	tokensToHundredths,
	DbTimestamp,
} from '@run-diffusion/shared';
import { functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import {
	TeamUserMixin,
	MyBalanceMixin,
} from '@/mixins';
import { deriveTopUpPackQuantities } from '@/utils/topUpPacks';
import TokensSVG from '@/assets/TokensSVG.vue';
import ThresholdSlider from '@/components/AutoTopUpSettings/ThresholdSlider.vue';

/**
 * Auto top-up section of the token dialog (v3, from the design handoff).
 * Four states: on/collapsed · off/nudge · loading skeleton · expanded edit form.
 * Flipping the toggle on opens the form rather than saving a guessed default — nothing persists
 * until the primary button. The payment method is read-only here (resolved server-side); changing
 * it goes through the billing portal.
 */
export default Vue.extend({
	name: 'AutoTopUpSettings',
	mixins: [
		TeamUserMixin,
		MyBalanceMixin,
	],
	props: {
		active: { type: Boolean, default: false }, // Dialog open + this variant visible
		// The parent dialog owns the getRunnitTokensInfo round trip (it needs the same payload for
		// the upgrade row), and hands the result down so we only call it once per open.
		info: { type: Object, default: null },
		loadingInfo: { type: Boolean, default: false },
	},
	data () {
		return {
			saving: false,
			formMode: null as null | 'setup' | 'editing',
			formThreshold: 0, // hundredths
			formQty: 1,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'userBalanceAccount',
			'teamBalanceAccount',
			'stripeTeamsCustomer',
		]),
		...mapGetters([
			'displayLabels',
			'companyClubOfferBenefits',
		]),
		liveAutoTopUp (): BalanceAccountAutoTopUp {
			return this._get(this.activeBalanceAccount, 'autoTopUp') || null;
		},
		liveEnabled (): boolean {
			return !!this._get(this.liveAutoTopUp, 'enabled');
		},
		liveSettings (): { thresholdRunnitTokens: number, qty: number } {
			return {
				thresholdRunnitTokens: this._get(this.liveAutoTopUp, 'thresholdRunnitTokens') || 0,
				qty: this._get(this.liveAutoTopUp, 'qty') || 1,
			};
		},
		unitTokens (): number {
			if (this._get(this.info, 'unitTokens')) return this.info.unitTokens;
			if (!this.onPersonalPlan) return 0;
			return this._get(this.user, 'clubInfo.token.oneTimeTopUp.tokens') || 0;
		},
		unitPriceAmount (): number {
			if (this._get(this.info, 'unitPriceAmount')) return this.info.unitPriceAmount;
			if (!this.onPersonalPlan) return 0;
			return this._get(this.user, 'clubInfo.token.oneTimeTopUp.stripe.priceAmount') || 0;
		},
		autoModeMinQty (): number {
			return Math.max(this._get(this.info, 'autoModeMinQty') || 1, 1);
		},
		maxQty (): number {
			if (this._get(this.info, 'maxQty')) return this.info.maxQty;
			if (!this.onPersonalPlan) return 1;
			return this._get(this.user, 'clubInfo.token.oneTimeTopUp.maxQty') || 1;
		},
		liveBuyAmount (): number {
			return this.liveSettings.qty * (this.unitTokens || 0);
		},
		isEligible (): boolean {
			if (!this.onPersonalPlan) {
				if (!this._get(this.team, 'isActive')) return false;
				// Enterprise teams buy tokens through their account manager, never a payment method on file
				if (this.companyClubOfferBenefits.isEnterprise) return false;
				return !(this.stripeTeamsCustomer && !this._get(this.stripeTeamsCustomer, 'stripeId'));
			}
			// A paid plan whose token add-on actually sells a top-up pack; there is nothing to buy without one
			return !!(
				!this._get(this.user, 'clubOffer.isFreemium') &&
				this._get(this.user, 'clubInfo.token.oneTimeTopUp.tokens')
			);
		},
		isAutoTopUpAvailable (): boolean {
			return this.isEligible && (this.canManage || this.liveEnabled);
		},
		canManage (): boolean {
			return !!(this.onPersonalPlan || this.hasManageCompanyPermission);
		},
		// Disabled WITH a failure on record means WE turned it off; the user switching it off clears
		// those fields (both paths in updateAutoTopUpSettings do), so the two states stay distinct.
		isPaused (): boolean {
			return !!(
				this.liveAutoTopUp &&
				!this.liveAutoTopUp.enabled &&
				this.liveAutoTopUp.lastFailureAt
			);
		},
		showSkeleton (): boolean {
			return this.loadingInfo && !this.info;
		},
		isFormOpen (): boolean {
			return this.formMode !== null;
		},
		isEditingMode (): boolean {
			return this.formMode === 'editing';
		},
		formSubtitle (): string {
			if (!this.isEditingMode) return 'Not active yet. Save these settings to turn it on.';
			const activeSince: DbTimestamp = this._get(this.liveAutoTopUp, 'updatedAt');
			const lastAt: DbTimestamp = this._get(this.liveAutoTopUp, 'lastSuccessAt');
			const lastAmount: number = this._get(this.liveAutoTopUp, 'lastSuccessRunnitTokensAmount') || 0;
			const parts: string[] = [];
			if (activeSince && activeSince.toMillis) parts.push(`Active since ${moment(activeSince.toMillis()).format('MMM D')}`);
			if (lastAt && lastAt.toMillis && lastAmount) parts.push(`last bought ${currencyTokens(hundredthsToTokens(lastAmount))} on ${moment(lastAt.toMillis()).format('MMM D')}`);
			return parts.length ? parts.join(' · ') : 'Changes are not active until you save.';
		},
		// Pack multiples of the plan's top-up unit, capped by maxQty (1× 2× 5× 10× 20×)
		// Manual buying still starts at 1x; auto top-up is floored per plan so heavy users aren't
		// drip-charged an amount worth about a day of their usage.
		packOptions (): { qty: number, tokens: number, priceCents: number, tokensDisplay: string, priceDisplay: string }[] {
			if (!(this.unitTokens > 0)) return [];
			return deriveTopUpPackQuantities({ minQty: this.autoModeMinQty, maxQty: this.maxQty })
				.map((qty: number) => ({
					qty,
					tokens: qty * this.unitTokens,
					priceCents: qty * this.unitPriceAmount,
					tokensDisplay: currencyTokens(hundredthsToTokens(qty * this.unitTokens)),
					priceDisplay: qty * this.unitPriceAmount > 0 ? currency(centsToDollars(qty * this.unitPriceAmount)) : '',
				}));
		},
		selectedPack (): any {
			return this.packOptions.find(({ qty }) => qty === this.formQty) || this.packOptions[0] || null;
		},
		selectedPackPriceDisplay (): string {
			if (!this.selectedPack || !this.selectedPack.priceCents) return '';
			return `for ${currency(centsToDollars(this.selectedPack.priceCents))}`;
		},
		maxThreshold (): number {
			return this.formQty * (this.unitTokens || 0);
		},
		thresholdError (): string {
			// Short and literal: the field is already labelled, so the error only has to say what to do
			if (!(this.formThreshold > 0)) return 'Enter an amount above 0';
			if (this.formThreshold > this.maxThreshold) {
				return `Cannot exceed ${currencyTokens(hundredthsToTokens(this.maxThreshold))}`;
			}
			return '';
		},
		/*
		Saving while already below the threshold makes the server buy immediately (SETTINGS_KICK).
		The button and the line above it have to say so: the same calcRealtimeBalanceInfo the
		server checks against, so the UI can't promise one thing and the charge do another.
		 */
		currentRunnitTokens (): number {
			if (!this.activeBalanceAccount) return 0;
			const { realtimeRunnitTokens } = calcRealtimeBalanceInfo(this.activeBalanceAccount);
			return Math.max(realtimeRunnitTokens || 0, 0);
		},
		// The same judgement updateAutoTopUpSettings makes before its immediate top-up, in the same
		// whole tokens the form shows, so "Buys X tokens when you save" is never a guess
		willBuyOnSave (): boolean {
			return !!(
				!this.thresholdError &&
				this.formThreshold > 0 &&
				isBelowAutoTopUpThreshold(this.currentRunnitTokens, this.formThreshold)
			);
		},
		saveOutcome (): { title: string, detail: string, price: string } | null {
			if (this.thresholdError || !(this.formThreshold > 0) || !this.selectedPack) return null;
			const buyAmount: string = currencyTokens(hundredthsToTokens(this.selectedPack.tokens || 0));
			const price: string = this.selectedPack.priceCents ? currency(centsToDollars(this.selectedPack.priceCents)) : '';
			const balance: string = currencyTokens(hundredthsToTokens(this.currentRunnitTokens));
			const threshold: string = currencyTokens(hundredthsToTokens(this.formThreshold));
			if (this.willBuyOnSave) {
				return {
					title: `Buys ${buyAmount} tokens when you save these settings`,
					detail: `Your balance is ${balance}, already under ${threshold}.`,
					price,
				};
			}
			return {
				title: 'Nothing is charged now',
				detail: `Buys ${buyAmount} tokens${price ? ` for ${price}` : ''} once you drop under ${threshold}. Your balance is ${balance}.`,
				price: '',
			};
		},
		savePrimaryLabel (): string {
			if (this.willBuyOnSave) {
				const price: string = this.selectedPack && this.selectedPack.priceCents
					? ` · ${currency(centsToDollars(this.selectedPack.priceCents))}`
					: '';
				return `${this.isEditingMode ? 'Save and buy now' : 'Turn on and buy now'}${price}`;
			}
			return this.isEditingMode ? 'Save changes' : 'Turn on auto top-up';
		},
		canSave (): boolean {
			return !!(
				!this.thresholdError &&
				this.formQty >= 1 &&
				this.info &&
				this.info.paymentMethod
			);
		},
		hasDefaultPaymentMethod (): boolean {
			return !!this._get(this.info, 'paymentMethod');
		},
		// A null `info` means "not loaded yet", not "no payment method" — don't cry wolf before we've heard back
		missingDefaultPaymentMethod (): boolean {
			return !!(this.info && !this.hasDefaultPaymentMethod);
		},
		// Cards by brand and last 4; the wallets auto top-up can charge by name
		paymentMethodDisplay (): string {
			const paymentMethod: any = this._get(this.info, 'paymentMethod');
			if (!paymentMethod) return this.loadingInfo ? 'Loading…' : 'No default payment method';
			if (paymentMethod.type === 'cashapp') return 'Cash App Pay';
			if (paymentMethod.type === 'alipay') return 'Alipay';
			if (paymentMethod.type === 'link') return 'Link';
			const brand: string = (paymentMethod.brand || 'Card');
			return `${brand.charAt(0).toUpperCase()}${brand.slice(1)} ···· ${paymentMethod.last4}`;
		},
		// The card's expiry, or whatever identifies the wallet account (Alipay gives nothing)
		paymentMethodDetailDisplay (): string {
			const paymentMethod: any = this._get(this.info, 'paymentMethod');
			if (!paymentMethod) return '';
			if (paymentMethod.type === 'cashapp') return paymentMethod.cashtag || '';
			if (paymentMethod.type === 'link') return paymentMethod.linkEmail || '';
			if (!paymentMethod.expMonth) return '';
			return `expires ${String(paymentMethod.expMonth).padStart(2, '0')}/${String(paymentMethod.expYear).slice(-2)}`;
		},
		paymentMethodIcon (): string {
			const type: string = this._get(this.info, 'paymentMethod.type') || 'card';
			return type === 'card' ? 'mdi-credit-card-outline' : 'mdi-wallet-outline';
		},
	},
	watch: {
		packOptions (options: any[]) {
			if (!options.length) return;
			if (!options.some(({ qty }) => qty === this.formQty)) {
				this.formQty = options[0].qty;
			}
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'goToStripeSubscriptionPortal',
		]),
		hundredthsToTokens,
		tokensToHundredths,
		openPortal () {
			this.goToStripeSubscriptionPortal(this.onPersonalPlan ? null : this.team);
		},
		seedForm () {
			this.formQty = Math.min(Math.max(this.liveSettings.qty || this.autoModeMinQty, this.autoModeMinQty), this.maxQty);
			// First-time default: a quarter of one top-up, so it buys a few times a cycle rather than
			// once in one big hit. Stays in hundredths — ThresholdSlider snaps whatever it is handed,
			// including a threshold a plan change has since put out of range, so there is nothing to
			// round here and no reason to detour through tokens.
			const saved: number = this.liveSettings.thresholdRunnitTokens || 0;
			const fallback: number = Math.round(0.25 * this.formQty * (this.unitTokens || 0));
			this.formThreshold = saved > 0 ? saved : fallback;
		},
		openSetup () {
			// Opens the form rather than saving a guessed default — nothing persists until Save
			this.seedForm();
			this.formMode = 'setup';
		},
		openEditing () {
			this.seedForm();
			this.formMode = 'editing';
		},
		closeForm () {
			// Cancel: setup returns the toggle to off; editing discards changes
			this.formMode = null;
		},
		async save () {
			if (this.saving || !this.canSave) return;
			this.saving = true;
			try {
				const functionRef = functions.httpsCallable('updateAutoTopUpSettings');
				const { success, message } = (await functionRef({
					balanceAccountType: this.onPersonalPlan ? 'USER' : 'TEAM',
					...(!this.onPersonalPlan && { teamId: this._get(this.team, 'id') }),
					enabled: true,
					thresholdRunnitTokens: this.formThreshold,
					qty: this.formQty,
				})).data;
				this.updateSnackbar({
					status: success ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
					message: message || (success ? 'Auto top-up saved' : 'Error saving auto top-up settings'),
					show: true,
				});
				if (success) this.formMode = null;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error saving auto top-up settings, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
		async saveDisable () {
			if (this.saving) return;
			this.saving = true;
			try {
				const functionRef = functions.httpsCallable('updateAutoTopUpSettings');
				const { success, message } = (await functionRef({
					balanceAccountType: this.onPersonalPlan ? 'USER' : 'TEAM',
					...(!this.onPersonalPlan && { teamId: this._get(this.team, 'id') }),
					enabled: false,
				})).data;
				this.updateSnackbar({
					status: success ? SNACKBAR_STATUS.SUCCESS : SNACKBAR_STATUS.ERROR,
					message: message || (success ? 'Auto top-up turned off' : 'Error turning off auto top-up'),
					show: true,
				});
				if (success) this.formMode = null;
			} catch (e) {
				console.error(e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error turning off auto top-up, please try again',
					show: true,
				});
			} finally {
				this.saving = false;
			}
		},
	},
	components: {
		TokensSVG,
		ThresholdSlider,
	},
});
